import { useMemo } from 'react';
import { Card } from '../../components/Card';
import SortableTable from '../../components/SortableTable';
import { Input } from '../../catalyst/input';
import { Link } from '../../components/Link';
import { IconCross } from '../../icons/IconCross';
import { formatDateTime } from '../../lib/dateFormat';
import { confidenceBandText } from '../../lib/confidenceBand';
import { PageSequenceStrip } from './PageSequenceStrip';
import type { CategoryPages } from './types';

export function PagesTable({
  pages,
  search,
  setSearch,
}: {
  pages: CategoryPages;
  search: string;
  setSearch: (search: string) => void;
}) {
  const rows = useMemo(
    () =>
      pages.map((page) => ({
        ...page,
        margin: Math.max(0, (page.classification?.[0]?.[1] ?? 0) - (page.classification?.[1]?.[1] ?? 0)),
      })),
    [pages],
  );

  return (
    <Card>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">Pages</h2>

      <PageSequenceStrip pages={pages} />

      <div className="flex flex-col md:flex-row md:items-center gap-4 mt-4 mb-4">
        <div className="relative w-full md:w-64">
          <Input
            placeholder="Search by Page ID or Label..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-sm"
            inputClassName="pr-9"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute inset-y-0 right-0 z-10 pr-3 flex items-center"
              aria-label="Clear search"
            >
              <IconCross className="h-5 w-5 text-gray-400 dark:text-zinc-400 hover:text-gray-600 dark:hover:text-zinc-300" />
            </button>
          )}
        </div>
      </div>
      <div className="mt-2">
        <SortableTable
          storageId="category-pages"
          columns={[
            {
              label: 'Page ID',
              accessor: 'id',
              sortable: true,
              render: (page) => (
                <Link
                  to={`/document-pages/${page.id}`}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {page.id.slice(0, 8)}...{page.id.slice(-8)}
                </Link>
              ),
            },
            {
              label: 'Category',
              accessor: 'classificationCategory',
              sortable: true,
              render: (row) => row.classificationCategory,
            },
            {
              label: 'Confidence',
              accessor: 'classificationScore',
              sortable: true,
              render: (page) => (
                <span className={confidenceBandText(page.classificationScore)}>
                  {(page.classificationScore * 100).toFixed(0)}%
                </span>
              ),
            },
            {
              label: 'Margin',
              accessor: 'margin',
              sortable: true,
              render: (page) => <>{(page.margin * 100).toFixed(0)}%</>,
            },
            {
              label: 'Created',
              accessor: 'createdAt',
              sortable: true,
              render: (page) => formatDateTime(page.createdAt),
            },
          ]}
          data={rows}
          rowKey={(row) => row.id}
        />
      </div>
    </Card>
  );
}
